import BaseComponent from '../base-component/base-component'
import { bodyElement } from '../../components/common/body-element'
import { delay, checkEscKey, getTransitionDuration } from '../../utils/utils'
import {
  getStylePropValue,
  getTemplateFirstChildContent,
} from '../../utils/dom'
import { Nullable } from '../../types/common'

class Sidebar extends BaseComponent {
  template: Nullable<HTMLTemplateElement>
  element: HTMLElement
  buttonSelector: string
  touchStartX: number
  touchStartY: number
  touchEndX: number
  touchEndY: number
  styles: CSSStyleDeclaration

  constructor() {
    super()

    this.template = this.isOpenOnInit
      ? null
      : this.querySelector(`[data-sidebar-template="${this.id}"]`)
    this.element = this.isOpenOnInit
      ? this.querySelector('[data-sidebar-body]')
      : getTemplateFirstChildContent(this.template)

    this.buttonSelector = `[data-sidebar-button][data-sidebar-id="${this.id}"]`
    this.styles = getComputedStyle(this)
  }

  connectedCallback(): void {
    this.addEventListener('touchstart', this.handleTouchStart)
    this.addEventListener('touchmove', this.handleTouchMove)
    this.addEventListener('touchend', this.handleTouchEnd)

    if (this.isEditorMode) {
      this.editor.subscribe('SECTION_LOAD', this.handleSectionLoad)
      this.editor.subscribe('SECTION_SELECT', this.handleSectionSelect)
      this.editor.subscribe('SECTION_DESELECT', this.handleSectionDeselect)
    }
  }

  disconnectedCallback(): void {
    this.removeEventListener('touchstart', this.handleTouchStart)
    this.removeEventListener('touchmove', this.handleTouchMove)
    this.removeEventListener('touchend', this.handleTouchEnd)

    if (this.isEditorMode) {
      this.editor.destroy()
    }
  }

  handleSectionLoad = (): void => {
    if (!this.isSection) {
      this.hide()
    }
  }

  handleSectionSelect = ({ detail: { sectionId } }): void => {
    if (this.isSection && this.dataset.sectionId === sectionId) {
      this.open()
    }
  }

  handleSectionDeselect = ({ detail: { sectionId } }): void => {
    if (this.isSection && this.dataset.sectionId === sectionId) {
      this.hide()
    }
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const overlay = document.getElementById('BodyOverlay')
    const overlayZIndex = parseFloat(getStylePropValue(overlay, 'zIndex'))
    const zIndex = parseFloat(this.styles['zIndex']) - 1
    const isOverlay = event.target === overlay

    const isOutsideClick = isOverlay && overlayZIndex <= zIndex

    if (isOutsideClick && this.isOpen) {
      this.hide()
    }
  }

  handleTouchStart = (event: TouchEvent): void => {
    this.touchStartX = event.targetTouches[0].clientX
    this.touchStartY = event.targetTouches[0].clientY
  }

  handleTouchMove = (event: TouchEvent): void => {
    this.touchEndX = event.targetTouches[0].clientX
    this.touchEndY = event.targetTouches[0].clientY
  }

  handleTouchEnd = (event): void => {
    const X_DIRECTION_THRESHOLD = 96
    const Y_DIRECTION_THRESHOLD = 48

    const distanceX = Math.abs(this.touchStartX - this.touchEndX)
    const distanceY = Math.abs(this.touchStartY - this.touchEndY)
    const isCarousel = event.target.closest('[data-carousel-viewport]')

    if (isCarousel) {
      return
    }

    if (this.touchEndX === 0) {
      return
    }

    if (distanceY > Y_DIRECTION_THRESHOLD) {
      return
    }

    const isTrigger =
      this.dataset.openDirection === 'right'
        ? this.touchStartX < this.touchEndX
        : this.touchStartX > this.touchEndX

    if (this.isOpen && distanceX > X_DIRECTION_THRESHOLD && isTrigger) {
      this.hide()
    }

    this.cleanTouchPoints()
  }

  handleKeydown = (event: KeyboardEvent): void => {
    const key = event.key || event.keyCode

    const withCloseOnEscape = this.hasAttribute('data-with-close-on-escape')
    const isEscape = checkEscKey(key)

    if (withCloseOnEscape && isEscape) {
      this.hide()
    }
  }

  cleanTouchPoints(): void {
    this.touchStartX = 0
    this.touchEndX = 0
    this.touchStartY = 0
    this.touchEndY = 0
  }

  open(): Promise<void> {
    this.mount()

    this.setAttribute('tabindex', '-1')
    this.focus()

    setTimeout(() => {
      this.setOpenState(true)
    }, 0)

    return delay(getTransitionDuration(this.styles))
  }

  hide(isInstant?: boolean): any {
    this.setOpenState(false)

    if (isInstant) {
      this.unmount()
    } else {
      return delay(getTransitionDuration(this.styles)).then(() => {
        this.unmount()
      })
    }
  }

  toggle(): void {
    if (this.isOpen) {
      this.hide()
    } else {
      this.open()
    }
  }

  mount(): void {
    if (!this.contains(this.element)) {
      this.appendChild(this.element)
    }
  }

  unmount(): void {
    if (this.element) {
      this.element.remove()
    }
  }

  updateTemplate(template: HTMLTemplateElement): void {
    this.element.remove()

    this.element = getTemplateFirstChildContent(template)

    this.appendChild(this.element)
  }

  setOpenState(isOpen: boolean): void {
    const withOverlay = this.hasAttribute('data-with-overlay')
    const buttons = document.querySelectorAll(this.buttonSelector)

    this.setVisible(isOpen)
    this.setListeners(isOpen ? 'add' : 'remove')

    if (withOverlay) {
      const isImportant = this.hasAttribute('data-overlay-important')
      const withBlur = this.hasAttribute('data-overlay-with-blur')
      const overlayOpacity = +(this.getAttribute('data-overlay-opacity') || 50)

      const styles = {
        zIndex: isImportant ? parseFloat(this.styles['zIndex']) - 1 : 3,
        backdropFilter: withBlur ? `blur(16.5px)` : 'none',
        backgroundColor: `var(--gsc-black-color-${overlayOpacity})`,
      }

      isOpen
        ? bodyElement.showOverlay(this.id, styles)
        : delay(getTransitionDuration(this.styles)).then(() => {
            bodyElement.hideOverlay(this.id)
          })
    }

    buttons.forEach(button => {
      button.setAttribute('data-aria-expanded', isOpen ? 'true' : 'false')
    })
  }

  setVisible(isVisible: boolean): void {
    this.setAttribute('data-aria-expanded', isVisible ? 'true' : 'false')
  }

  setListeners(action: string): void {
    const withOutsideClick = this.hasAttribute('data-with-close-on-outside')
    const eventListenerSetter = `${action}EventListener`

    if (withOutsideClick) {
      window[eventListenerSetter]('click', this.handleOutsideClick)
    }

    window[eventListenerSetter]('keydown', this.handleKeydown)
  }

  get isSection(): boolean {
    return this.hasAttribute('data-sidebar-section')
  }

  get isOpen(): boolean {
    return this.getAttribute('data-aria-expanded') === 'true'
  }

  get isOpenOnInit(): boolean {
    return this.hasAttribute('is-open-on-init')
  }
}

export default Sidebar
